import type { Location } from "react-router";
import {
  createContext,
  useCallback,
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router";
import type { Serialiser } from "hooks/serialisers/types";

type HistoryMode = "push" | "replace";
interface QueryNavigation {
  search: string;
  update: (
    key: string,
    update: (previous: string | null) => string | null,
    history: HistoryMode
  ) => void;
}
const NavigationQueryContext = createContext<QueryNavigation | null>(null);

export const NavigationQueryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location: Location<unknown> = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const requestedNavigationsRef = useRef(new Set<string>());
  const pendingRef = useRef<{
    locationKey: string;
    params: URLSearchParams;
    history: HistoryMode;
    scheduled: boolean;
  } | null>(null);
  const currentLocationRef = useRef(location);
  useLayoutEffect(() => {
    currentLocationRef.current = location;
    const pending = pendingRef.current;
    const requested = requestedNavigationsRef.current.delete(
      `${location.pathname}${location.search}${location.hash}`
    );
    if (
      navigationType !== NavigationType.Pop &&
      requested &&
      pending != null &&
      pending.params.toString() !== location.search.replace(/^\?/, "")
    ) {
      pending.locationKey = location.key;
    } else {
      pendingRef.current = null;
      requestedNavigationsRef.current.clear();
    }
  }, [location, navigationType]);
  useEffect(
    () => () => {
      pendingRef.current = null;
    },
    []
  );
  const update = useCallback<QueryNavigation["update"]>(
    (key, updateParameter, history) => {
      const current = currentLocationRef.current;
      const queued = pendingRef.current;
      const batch =
        queued?.locationKey === current.key
          ? queued
          : {
              locationKey: current.key,
              params: new URLSearchParams(current.search),
              history,
              scheduled: false,
            };
      const previous = batch.params.get(key);
      const next = updateParameter(previous);
      if (previous === next) return;
      if (next == null) batch.params.delete(key);
      else batch.params.set(key, next);
      if (history === "push") batch.history = "push";
      pendingRef.current = batch;
      if (batch.scheduled) return;
      batch.scheduled = true;
      queueMicrotask(() => {
        if (pendingRef.current !== batch) return;
        batch.scheduled = false;
        if (currentLocationRef.current.key !== batch.locationKey) return;
        const current = currentLocationRef.current;
        const search = batch.params.toString();
        requestedNavigationsRef.current.add(
          `${current.pathname}${search ? `?${search}` : ""}${current.hash}`
        );
        const replace = batch.history === "replace";
        batch.history = "replace";
        void navigate(
          {
            pathname: current.pathname,
            search,
            hash: current.hash,
          },
          { replace, preventScrollReset: true, state: current.state }
        );
      });
    },
    [navigate]
  );
  const context = useMemo(
    () => ({ search: location.search, update }),
    [location.search, update]
  );
  return (
    <NavigationQueryContext value={context}>{children}</NavigationQueryContext>
  );
};

export const useNavigationQuery = <T,>(
  key: string,
  initial: T | (() => T),
  serialiser: Serialiser<T, never>,
  history: HistoryMode
): [T, (update: SetStateAction<T>) => void] => {
  const context = use(NavigationQueryContext);
  if (context == null)
    throw new Error("ScrapEye navigation requires its query provider");
  const { search, update } = context;
  const [fallback] = useState(initial);
  const text = new URLSearchParams(search).get(key);
  const selection = useMemo(() => {
    const parsed = text == null ? null : serialiser.parse(text);
    return parsed?.valid ? parsed.value : fallback;
  }, [text, serialiser, fallback]);
  useEffect(() => {
    if (text != null && !serialiser.parse(text).valid)
      update(
        key,
        (previous) =>
          previous != null && serialiser.parse(previous).valid
            ? previous
            : null,
        "replace"
      );
  }, [text, serialiser, key, update]);
  const omitDefault = typeof initial !== "function";
  const setSelection = useCallback(
    (next: SetStateAction<T>) => {
      update(
        key,
        (previous) => {
          const parsed = previous == null ? null : serialiser.parse(previous);
          const current = parsed?.valid ? parsed.value : fallback;
          const selection =
            typeof next === "function"
              ? (next as (current: T) => T)(current)
              : next;
          const formatted = serialiser.format(selection);
          if (!serialiser.parse(formatted).valid) return previous;
          return omitDefault && formatted === serialiser.format(fallback)
            ? null
            : formatted;
        },
        history
      );
    },
    [key, serialiser, fallback, omitDefault, history, update]
  );
  return [selection, setSelection];
};
